import React, { useState } from 'react';
import { 
  Box, 
  Drawer, 
  IconButton, 
  Typography, 
  List, 
  ListItem, 
  Divider, 
  Badge,
  Tooltip,
  Link
} from '@mui/material';
import { Source, Close, PictureAsPdf, Language } from '@mui/icons-material';
import { Citation } from '../types/citation';
import { useAppConfig } from '../contexts/ConfigContext';
import { useConversation } from '../contexts/ConversationContext';
import { isPDFCitation, getPDFViewerUrl } from '../utils/pdfUtils';

interface CitationFlyoutProps {
  citations: Citation[];
  messageId?: string; // ID of the message containing these citations
}

const CitationFlyout: React.FC<CitationFlyoutProps> = ({ citations, messageId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { config } = useAppConfig();
  const { sendCitationClick } = useConversation();
    const handleCitationClick = (citation: Citation, citationIndex: number, event?: React.MouseEvent) => {
    // Track citation click via WebSocket
    if (sendCitationClick) {
      sendCitationClick(citation.url, citation.title, citationIndex, messageId);
    }
    
    // Check if this is a PDF citation
    if (isPDFCitation(citation)) {
      // Prevent default link behavior
      if (event) {
        event.preventDefault();
      }
      
      // Open PDF in PDF.js viewer
      const pdfViewerUrl = getPDFViewerUrl(citation.url);
      window.open(pdfViewerUrl, '_blank', 'noopener,noreferrer');
      
      return false; // Prevent Link component default behavior
    }
    
    // For non-PDF citations, let the Link component handle normally
  };

  if (citations.length === 0) {
    return null;
  }

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Helper function to add transparency to colors (hex or rgba)
  const addTransparency = (color: string, alpha: number = 0.5): string => {
    if (!color) return 'rgba(25, 118, 210, 0.08)';
    
    // If it's already an rgba value, increase the alpha slightly for hover
    if (color.startsWith('rgba')) {
      const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
      if (match) {
        const [, r, g, b, a] = match;
        const newAlpha = Math.min(parseFloat(a) + 0.05, 1); // Increase alpha by 0.05
        return `rgba(${r}, ${g}, ${b}, ${newAlpha})`;
      }
    }
    
    // If it's a hex value, convert to rgba with transparency
    if (color.startsWith('#')) {
      return `${color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
    }
    
    // Fallback
    return color;
  };

  return (
    <>
      {/* Sources Button */}
      <Tooltip title={
        config?.ui_tooltips?.sources_button_tooltip || 
        `View ${citations.length} source${citations.length > 1 ? 's' : ''} ${
          citations.some(isPDFCitation) ? '(includes PDF documents)' : ''
        }`
      }>
        <Badge 
          badgeContent={citations.length} 
          sx={{ 
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              minWidth: '16px',
              height: '16px',
              backgroundColor: config?.ui_theme?.sources_button_count_background || '#1976d2',
              color: config?.ui_theme?.sources_button_count_text || '#ffffff'
            }
          }}
        >
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{
              color: config?.ui_theme?.sources_button_icon || 'primary.main',
              backgroundColor: config?.ui_theme?.sources_button_background || 'rgba(25, 118, 210, 0.04)',
              border: `1px solid ${config?.ui_theme?.sources_button_border || 'rgba(25, 118, 210, 0.12)'}`,
              borderRadius: 1,
              padding: '4px 8px',
              fontSize: '0.75rem',
              fontWeight: 500,
              gap: 0.5,
              '&:hover': {
                backgroundColor: config?.ui_theme?.sources_button_background 
                  ? `${config.ui_theme.sources_button_background}80` // Add some opacity for hover
                  : 'rgba(25, 118, 210, 0.08)',
                borderColor: config?.ui_theme?.sources_button_border || 'rgba(25, 118, 210, 0.24)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <Source sx={{ fontSize: 14 }} />            <Typography variant="caption" sx={{ 
              fontSize: '0.7rem', 
              fontWeight: 500,
              color: config?.ui_theme?.sources_button_text || '#1976d2'
            }}>
              {config?.ui_text?.sources_button_label || "Sources"}
            </Typography>
          </IconButton>
        </Badge>
      </Tooltip>

      {/* Citation Flyout Drawer */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            maxWidth: '90vw'
          }
        }}
        ModalProps={{
          keepMounted: false
        }}
      >
        <Box sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          bgcolor: 'background.paper'
        }}>          {/* Header */}
          <Box sx={{ 
            p: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: config?.ui_theme?.citations_flyout_title_background || 'rgba(25, 118, 210, 0.04)',
            color: config?.ui_theme?.citations_flyout_title_text || '#1976d2',
            minHeight: 48
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Source sx={{ 
                color: config?.ui_theme?.citations_flyout_title_icon || '#1976d2' 
              }} />              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600,
                color: config?.ui_theme?.citations_flyout_title_text || '#1976d2',
                fontSize: '1rem'
              }}>
                {config?.ui_citations_flyout?.title || "Sources"}
              </Typography>
              {/* <Badge 
                badgeContent={citations.length} 
                color="secondary"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: 'primary.main',
                    fontWeight: 600
                  }
                }}
              /> */}
            </Box>            <IconButton 
              onClick={handleClose} 
              sx={{ 
                color: config?.ui_theme?.citations_flyout_title_icon || '#1976d2',
                '&:hover': {
                  backgroundColor: addTransparency(
                    config?.ui_theme?.citations_flyout_title_background || 'rgba(25, 118, 210, 0.04)',
                    0.5
                  )
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <List sx={{ px: 1 }}>
              {citations.map((citation, index) => {
                // Clean up URL for display (remove protocol and make it more readable)
                const displayUrl = citation.url.replace(/^https?:\/\//, '');
                
                // Extract domain for better organization
                const domain = displayUrl.split('/')[0];
                const isPDF = isPDFCitation(citation);

                return (
                  <React.Fragment key={index}>
                    <Link 
                      href={citation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      color="inherit"
                      onClick={(event) => handleCitationClick(citation, index, event)}
                    >
                      <ListItem 
                        sx={{ 
                          flexDirection: 'column', 
                          alignItems: 'flex-start',
                          px: 2,
                          py: 1.5,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            transform: 'translateX(4px)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              color: config?.ui_theme?.citations_number_color || 'primary.main',
                              minWidth: 24,
                              textAlign: 'center'
                            }}
                          >
                            {index + 1}.
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              {isPDF ? (
                                <PictureAsPdf sx={{ fontSize: 16, color: 'error.main' }} />
                              ) : (
                                <Language sx={{ fontSize: 16, color: 'primary.main' }} />
                              )}
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: config?.ui_theme?.citations_text_color || 'primary.main' }}>
                                {citation.title || domain}
                              </Typography>
                            </Box>
                            {citation.content && (
                              <Typography variant="body2" sx={{ color: config?.ui_theme?.citations_content_color || 'text.secondary', mb: 0.5 }}>
                                {citation.content.length > 120 ? `${citation.content.substring(0, 120)}...` : citation.content}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: config?.ui_theme?.citations_url_color || 'text.secondary', wordBreak: 'break-all', fontStyle: 'italic' }}>
                              {displayUrl}
                            </Typography>
                          </Box>
                        </Box>
                      </ListItem>
                    </Link>
                    {index < citations.length - 1 && (
                      <Divider sx={{ mx: 2 }} />
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          </Box>          {/* Footer */}
          <Box sx={{ 
            px: 1.5, 
            py: 0.75, 
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default'
          }}>            <Typography variant="caption" sx={{ 
              color: 'text.secondary', 
              fontStyle: 'italic',
              fontSize: '0.7rem'
            }}>
              {config?.ui_citations_flyout?.footer || "Click any source to open it in a new tab"}
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default CitationFlyout;
